/**
 * Quotes data service: async adapter over the Procurement/Quote API.
 * Returns the same object shapes the Quotes screens already use (QuoteNo, Items[], ...).
 */
import {
  GetAllQuotesApi,
  GetQuoteByIdApi,
  GetQuotablePrsApi,
  CreateQuoteApi,
  EditQuoteApi,
  DeleteQuoteApi,
} from "../../../../../Api/Procurement/Quote";
import { statusChipType } from "../../../../Common/StatusChip";

// Chip tone for quote and comparison statuses (the shared mapping leaves Awarded/Expired grey).
export const quoteChipType = (status) =>
  ({ Awarded: "success", "Not Awarded": "error", Expired: "error" })[status] ||
  statusChipType(status);

// Envelope -> Data, or throw the backend message so screens can toast it.
export const unwrap = (res) => {
  if (res?.data?.IsSuccess) {
    return res.data.Data;
  }
  throw new Error(res?.data?.Message || res?.data?.title || "Request failed");
};

export const day = (d) => (d ? String(d).slice(0, 10) : "");

// Signed file URL -> display name (drops the query string and the 32-hex upload prefix).
export const fileNameFromUrl = (url) => {
  if (!url) {
    return "";
  }
  const last = String(url).split("?")[0].split("/").pop();
  try {
    return decodeURIComponent(last).replace(/^[0-9a-f]{32}_/, "");
  } catch (e) {
    return last;
  }
};

const rate = (r) => (r == null ? 0 : Number(r));

const header = (q) => ({
  Id: q.Id,
  QuoteNo: q.QuoteNumber,
  VendorId: q.VendorId,
  VendorName: q.VendorName,
  ProjectId: q.ProjectId,
  ProjectName: q.ProjectName,
  RequisitionId: q.PurchaseRequisitionId || "",
  RequisitionNo: q.PrNumber || "",
  IsDirect: Boolean(q.IsDirect),
  QuoteDate: day(q.QuoteDate),
  ValidTill: day(q.ValidTill),
  PaymentTerms: q.PaymentTerms || "",
  DeliveryLeadTime: q.DeliveryLeadTime || "",
  FreightAmount: Number(q.FreightAmount) || 0,
  Status: q.Status,
  InComparison: Boolean(q.InComparison),
});

const withTotals = (base, items) => {
  const ItemsTotal = items.reduce((a, i) => a + i.TotalAmount, 0);
  return { ...base, Items: items, ItemsTotal, GrandTotal: ItemsTotal + base.FreightAmount };
};

export const getQuotes = async () => {
  const rows = unwrap(await GetAllQuotesApi()) || [];
  return rows.map((q) =>
    withTotals(
      header(q),
      (q.Items || []).map((i) => ({
        MaterialId: i.MaterialId,
        MaterialName: i.Material,
        Uom: i.Unit,
        Qty: Number(i.Quantity) || 0,
        UnitRate: rate(i.Rate),
        TotalAmount: rate(i.Rate) * (Number(i.Quantity) || 0),
      })),
    ),
  );
};

export const getQuoteById = async (id) => {
  const q = unwrap(await GetQuoteByIdApi(id));
  const items = (q.Lines || []).map((l) => ({
    id: l.Id,
    QuoteLineId: l.Id,
    PurchaseRequisitionLineId: l.PurchaseRequisitionLineId || "",
    MaterialId: l.MaterialId,
    MaterialName: l.Material,
    Specification: l.Brand || "",
    Uom: l.Unit,
    Qty: Number(l.Quantity) || 0,
    UnitRate: rate(l.Rate),
    TotalAmount: rate(l.Rate) * (Number(l.Quantity) || 0),
    Remarks: l.Remarks || "",
  }));
  return {
    ...withTotals(header(q), items),
    Remarks: q.Narration || "",
    AttachmentName: q.FileName || fileNameFromUrl(q.File),
    AttachmentUrl: q.File || "",
  };
};

// Requisitions that can be quoted, in the shape of the screen's old requisition list.
export const getQuotableRequisitions = async (projectId) => {
  const prs = unwrap(await GetQuotablePrsApi(projectId)) || [];
  return prs.map((p) => ({
    value: String(p.PurchaseRequisitionId),
    label: `${p.PrNumber} (${(p.Lines || []).map((l) => l.Material).join(" & ")})`,
    project: p.ProjectName,
    projectId: p.ProjectId,
    requisitionNo: p.PrNumber,
    items: (p.Lines || []).map((l) => ({
      id: l.PurchaseRequisitionLineId,
      PurchaseRequisitionLineId: l.PurchaseRequisitionLineId,
      MaterialName: l.Material,
      Specification: "",
      Uom: l.Unit,
      Qty: Number(l.OpenQuantity) || 0,
      EstimatedRate: l.EstimatedRate,
    })),
  }));
};

// q: the screen payload. Create when q.Id is empty, else Edit. Returns the API Data ({Id, QuoteNumber} on create).
export const saveQuote = async (q) => {
  const fd = new FormData();
  const add = (k, v) => {
    if (v !== undefined && v !== null && v !== "") {
      fd.append(k, v);
    }
  };
  add("PurchaseRequisitionId", q.RequisitionId);
  add("ProjectId", q.ProjectId);
  add("VendorId", q.VendorId);
  add("QuoteDate", q.QuoteDate);
  add("ValidTill", q.ValidTill);
  add("PaymentTerms", q.PaymentTerms);
  add("DeliveryLeadTime", q.DeliveryLeadTime);
  add("FreightAmount", q.FreightAmount);
  add("Narration", q.Remarks);
  if (q.File) {
    fd.append("File", q.File);
  } else if (q.RemoveFile) {
    fd.append("RemoveFile", "true");
  }
  q.Items.forEach((it, i) => {
    if (q.RequisitionId) {
      add(`Lines[${i}].PurchaseRequisitionLineId`, it.PurchaseRequisitionLineId);
    } else {
      add(`Lines[${i}].MaterialId`, it.MaterialId);
      add(`Lines[${i}].Quantity`, it.Qty);
    }
    add(`Lines[${i}].Rate`, Number(it.UnitRate) > 0 ? it.UnitRate : "");
    add(`Lines[${i}].Brand`, it.Specification);
    add(`Lines[${i}].Remarks`, it.Remarks);
  });
  const res = q.Id ? await EditQuoteApi(q.Id, fd) : await CreateQuoteApi(fd);
  return unwrap(res) || { Id: q.Id };
};

export const deleteQuote = async (id) => {
  unwrap(await DeleteQuoteApi(id));
  return getQuotes();
};
